import React, { Component } from 'react';
import t from 'prop-types';
import { Helmet } from 'rnd-helmet';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { AppContext } from 'appContext';
import maskSalary from 'maskSalary';
import getCountryName from 'getCountryName';
import textTransform from 'textTransform';

class SeoMetadata extends Component {
  /**
   * Builds schema JSON object for the salary results pages.
   *
   * @returns {Object}
   */
  getSchema = () => {
    const isCity = this.props.city && this.props.region;
    return {
      '@context': 'http://schema.org/',
      '@type': 'Occupation',
      name: this.props.occupation.job_title,
      description: this.props.occupation.job_desc,
      occupationLocation: [
        {
          '@type': isCity ? 'City' : 'Country',
          name: (isCity ? `${this.props.city}, ${this.props.region}, ` : '')
            + process.env.REACT_APP_COUNTRY.toUpperCase(),
        },
      ],
      estimatedSalary: [
        {
          '@type': 'MonetaryAmountDistribution',
          name: 'base',
          currency: process.env.REACT_APP_CURRENCY,
          duration: 'P1Y',
          median: this.props.salary,
        },
      ],
    };
  };

  render() {
    const props = { ...this.props };
    const { currencySettings: { currencySign, salarySeparator } } = this.context;

    const metaTitle = props.seoOverrides.metaTitle
      ? props.seoOverrides.metaTitle
      : props.intl.formatMessage({ id: 'Salary.MetaTitle' }, {
        occupation: textTransform(props.occupation.job_title),
        location: props.city && props.region ? `${props.city}, ${props.region}` : getCountryName(props.intl.locale),
        sitename: process.env.REACT_APP_SITENAME,
      });
    const metaDescription = props.seoOverrides.metaDescription
      ? props.seoOverrides.metaDescription
      : props.intl.formatMessage({ id: 'Salary.MetaDescription' }, {
        salary: `${currencySign}${maskSalary(Math.round(props.salary), salarySeparator)}`,
        occupation: textTransform(props.occupation.job_title),
        location: props.city || getCountryName(props.intl.locale),
      });

    return (
      <>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="title" content={metaTitle} />
          <meta name="og:title" content={metaTitle} />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="description" content={metaDescription} />
          <meta name="og:description" content={metaDescription} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="robots" content={(!!props.salary && props.seoOverrides.isAvailable) ? 'index' : 'noindex'} />
          <link rel="canonical" href={`${process.env.REACT_APP_BASE_URL}${props.match.url}`} />
          <meta name="og:url" content={`${process.env.REACT_APP_BASE_URL}${props.match.url}`} />
          <meta name="twitter:url" content={`${process.env.REACT_APP_BASE_URL}${props.match.url}`} />
          <meta name="og:image" content={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_OG_IMAGE}`} />
          <meta
            name="twitter:image"
            content={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_OG_TWITTER_IMAGE}`}
          />
        </Helmet>
        { /* No other way to add scripts in the footer with enabled SSR. */ }
        {/* eslint-disable xss/no-mixed-html, react/no-danger, react/no-array-index-key */}
        {!!props.salary && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(this.getSchema()) }} />}
        {!!props.salary && props.seoOverrides.structuredData.map((script, i) => (
          <script key={`structured_data_${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: script }} />
        ))}
        {/* eslint-enable xss/no-mixed-html, react/no-danger, react/no-array-index-key */}
      </>
    );
  }
}

SeoMetadata.contextType = AppContext;

SeoMetadata.propTypes = {
  seoOverrides: t.shape({
    metaTitle: t.string,
    metaDescription: t.string,
    isAvailable: t.bool,
    structuredData: t.arrayOf(t.string),
  }),
  city: t.string,
  region: t.string,
  occupation: t.shape({
    job_title: t.string.isRequired,
    job_desc: t.string,
  }),
  salary: t.number,
};

SeoMetadata.defaultProps = {
  seoOverrides: {
    metaTitle: '',
    metaDescription: '',
    isAvailable: false,
    structuredData: [],
  },
  city: '',
  region: '',
  occupation: {
    job_desc: '',
  },
  salary: 0,
};

export default injectIntl(withRouter(SeoMetadata));
